import * as React from "react";
import {
  Button,
  Box,
  Modal,
  SpaceBetween,
  Link,
  TableProps,
  StatusIndicator,
  PropertyFilter,
  ExpandableSection,
} from "@amzn/awsui-components-react/polaris";
import {
  awaitingPassException,
  awaitingPublishCorrectCatalogDefaults,
  awaitingGetCorrectCatalogDefaults,
  receivedGetCorrectCatalogDefaults as receivedGetCorrectCatalogDefaultsDispatch,
  createPassException,
  hideTableModal,
  setValidationModalState,
  showTableModal,
  validationModalInitialState,
  setCorrectCatalogDefaults,
  setCatalogDefaultsValuesLookup,
  publishCorrectCatalogDefault,
  setCorrectCatalogDefaultsExpandedVisible,
  setCorrectCatalogDefaultsExpandedNotVisible,
  setPassExceptionExpandedVisible,
  setPassExceptionExpandedNotVisible,
  filterDetailStepsLVS,
  sortDetailSteps,
  createPassExceptionNew,
  setSubmittedPublishCorrectCatalogDefaults,
} from "./validationAttributeModalConnector";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import {
  getIconForValidationAttribute,
  VALIDATION_ATTRIBUTE_RESULT,
  ValidationAttributeDetailStep,
} from "./validationAttribute";
import { EmptyValidationTableState } from "../porAsinList/TableCommonElements";
import logger from "../../art-logger-config";
import { AsinAudit, FullAsinWithAudits } from "../../server/asin-central/model";
import { decryptCustomerId } from "@amzn/amazon-id";
import { trackButtonClick } from "../telemetry/telemetry";
import {
  Table,
  Popover,
  Select,
  Input,
  Spinner,
  Alert,
  Container,
  Checkbox,
  TextContent,
} from "@amzn/awsui-components-react";
import { isWithin24Hours, serviceToDisplay } from "../../util/dateUtils";
import { AuthorizedAttribute } from "../../server/brass/model";
import {
  PASS_EXCEPTION_REASON_COLUMN_ID,
  RESULT_COLUMN_ID,
} from "../../constants/common";
import "../../styles/customEditTable.css"; // Overwriting polaris components, if naming changes on their end, this will no longer work
import "./validationAttributeModal.css";
import { PROPERTY_FILTER_I18N } from "../searchSideBar/searchPropertyFilter/SearchPropertyFilter-config";
import {
  PropertyFilterQuery,
  PropertyFilterToken,
} from "@amzn/awsui-collection-hooks/cjs/interfaces";
import { getCorrectCatalogDefaults } from "../app/api";
import { fetchPorAsins } from "../porAsinList/porAsinListSlice"; // Overwriting polaris components, if naming changes on their end, this will no longer work
import { useCollection } from "@amzn/awsui-collection-hooks";
import { getJourneysLink } from "../common/AsinDisplayCell";

function getSelectionCentralLinkForAsinMp(
  asin: FullAsinWithAudits,
  service: String
) {
  const obfuscatedMarketplaceId = asin.obfuscatedMarketplaceId;
  const marketplace = decryptCustomerId(obfuscatedMarketplaceId);
  const asinId = asin.asin;
  const linkString = `https://selection.amazon.com/item/${marketplace}/${asinId}`;
  return (
    <>
      <div>
        <>Click to see this ASIN in </>
        <Link
          external
          externalIconAriaLabel="Opens in a new tab"
          href={linkString}
        >
          Selection Central
        </Link>
      </div>
      {service == "LVS" ? (
        <div>
          <>Click to see this ASIN in </>
          <Link
            external
            externalIconAriaLabel="Opens in a new tab"
            href={getJourneysLink(asin.marketplace, asin.asin)}
          >
            Journeys
          </Link>
        </div>
      ) : (
        <></>
      )}
      {getValidationToolsInfoLink()}
    </>
  );
}

function cellWithAudit(val: string, audit: AsinAudit) {
  const entity =
    audit.lastUpdatedBy!.identity === "DeviceAsinListener"
      ? "Retail Catalog"
      : audit.lastUpdatedBy!.identity;
  return <>{val}</>;
}

export const LVS_ATTRIBUTES_IN_SCOPE = new Set<string>([
  "DISPLAY_ATTRIBUTE",
  "DISPLAY_ON_WEBSITE",
  "TITLE",
  "BRAND",
  "BRAND_CODE",
  "GL_PRODUCT_GROUP_TYPE",
  "PRODUCT_CATEGORY",
  "PRODUCT_SUBCATEGORY",
  "PRODUCT_TYPE",
  "SOR_ID",
  "CAN_BE_GIFT_WRAPPED",
  "CAN_BE_GIFT_MESSAGED",
  "ITEM_TYPE_KEYWORD",
  "MANUFACTURER",
  "RECOMMENDED_BROWSE_NODES",
  "FEATURE_BULLETS",
]);

function getValidationToolsInfoLink() {
  const linkString = `https://w.amazon.com/bin/view/PSLM/Working_with_us/ASIN_Central/Validation/#HPassExceptions`;
  return (
    <div>
      <>
        For more Information on Validations, Publishing Default Values, and Pass
        Exceptions — visit{" "}
      </>
      <Link
        external
        externalIconAriaLabel="Opens in a new tab"
        href={linkString}
      >
        Devices ASIN Central Wiki
      </Link>
    </div>
  );
}

export function displayDetailAttributeCell(
  attributeName: string,
  item?: ValidationAttributeDetailStep,
  formatter: (val: any) => string = (x) => x
) {
  if (item === undefined) {
    return `${formatter(attributeName)}`;
  }
  const audit = item.resultAudit;
  if (audit === undefined) {
    return `${formatter(attributeName)}`;
  }
  return cellWithAudit(formatter(attributeName), audit as AsinAudit);
}

export const ValidationModal = () => {
  const dispatch = useDispatch();
  const {
    isOpen,
    detailSteps,
    initialDetailSteps,
    detailStepsFailedLVS,
    initialDetailStepsFailedLVS,
    title,
    service,
    asin,
    waitingPassException,
    receivedPassException,
    correctCatalogDefaultsExpandedVisible,
    passExceptionExpandedVisible,
    waitingGetCorrectCatalogDefaults,
    receivedGetCorrectCatalogDefaults,
    waitingPublishCorrectCatalogDefaults,
    receivedPublishCorrectCatalogDefaults,
    catalogDefaultsValues,
    catalogDefaultsValuesLookup,
    submittedPublishCorrectCatalogDefaults,
  } = useSelector((state: RootState) => state.validationModal);
  const { editableAttributes } = useSelector(
    (state: RootState) => state.porAsinList
  );
  const lvsAuditDate = asin?.validationRequestLVSAudits?.lastUpdatedAt;
  const lvsRanWithin24Hours =
    lvsAuditDate != undefined && isWithin24Hours(new Date(lvsAuditDate));

  //TODO: Generalize this into the service contract
  let link = <></>;
  if (service === "LVS" && asin) {
    try {
      link = getSelectionCentralLinkForAsinMp(asin, service);
    } catch (e: any) {
      logger.error(
        `Error generating modal LVS link for ASIN ${asin.asin}: ${e.toString()}`
      );
    }
  }
  const initialDetailStepsHashMap = new Map(
    initialDetailSteps?.map((step) => [step.step, step])
  );
  const [calledGetCorrectCatalogDefaults, setCalledGetCorrectCatalogDefaults] =
    React.useState(false);
  if (
    service == "LVS" &&
    waitingGetCorrectCatalogDefaults &&
    detailStepsFailedLVS.length != 0 &&
    !calledGetCorrectCatalogDefaults
  ) {
    if (
      asin != null &&
      detailStepsFailedLVS.length != 0 &&
      lvsRanWithin24Hours
    ) {
      setCalledGetCorrectCatalogDefaults(true);
      getCorrectCatalogDefaults(
        asin.announceDate,
        asin.onlineStreetDate,
        asin.marketplace,
        asin.productLineId,
        asin.asinParty,
        asin.asinType,
        asin.itemType
      )
        .then((result) => {
          dispatch(setCorrectCatalogDefaults(result.catalogDefaultValues));
          dispatch(receivedGetCorrectCatalogDefaultsDispatch());
        })
        .catch(() => {
          dispatch(receivedGetCorrectCatalogDefaultsDispatch());
        });
    }
  }

  const anyPassExceptions = detailSteps.some((x) =>
    ["pass_exception", "pass_exception_one_time"].includes(x.result)
  );
  const anyErrorsInPassExceptionForm = detailSteps.some(
    (x) =>
      ["pass_exception", "pass_exception_one_time"].includes(x.result) &&
      !x.passExceptionReason
  );

  if (service === "Hattrick" && asin) {
    try {
      const hattrickWikiLink = `https://w.amazon.com/index.php/ADSOT/LEAP/Hattrick`;
      const quickSightLink = `https://us-east-1.quicksight.aws.amazon.com/sn/account/ador/dashboards/6091c54f-ac3e-4f2c-9742-547174464d83#p.paraASIN=${asin.asin}`;

      const selectionCentralLink = getSelectionCentralLinkForAsinMp(
        asin,
        service
      );
      link = (
        <div>
          <>
            For more information on Hattrick, including how to get access to
            validation dashboard, see{" "}
          </>
          <Link
            external
            externalIconAriaLabel="Opens in a new tab"
            href={hattrickWikiLink}
          >
            here
          </Link>
          <br />
          <>Review validation dashboard results for this ASIN in </>
          <Link
            external
            externalIconAriaLabel="Opens in a new tab"
            href={quickSightLink}
          >
            Quicksight
          </Link>
          {selectionCentralLink}
        </div>
      );
    } catch (e: any) {
      logger.error(
        `Error generating modal Hattrick link for ASIN ${
          asin.asin
        }: ${e.toString()}`
      );
    }
  }

  const [editHasBeenMade, setEditHasBeenMade] = React.useState(false);
  const [errorAfterSave, setErrorAfterSave] = React.useState(false);
  const [cancelModalVisible, setCancelModalVisible] = React.useState(false);
  const [
    correctCatalogDefaultsDetailSteps,
    setCorrectCatalogDefaultsDetailSteps,
  ] = React.useState(detailStepsFailedLVS);
  const [
    confirmCorrectCatalogDefaultsCheckBox,
    setConfirmCorrectCatalogDefaultsCheckBox,
  ] = React.useState(false);
  const hasCorrectValueBlank = (correctCatalogDefaultsDetailSteps || []).some(
    (item) => item.correctedValue === "<BLANK>"
  );

  const detailStepsResultWeight = (result: string): number => {
    const weights = {
      fail: 6,
      pass_exception: 5,
      pass_exception_one_time: 4,
      pass: 3,
      not_applicable: 2,
      exception: 1,
    };
    if (result in weights) {
      return weights[result as keyof typeof weights];
    } else {
      return -1;
    }
  };

  useEffect(() => {
    if (catalogDefaultsValues != undefined) {
      dispatch(setCatalogDefaultsValuesLookup(catalogDefaultsValues));
    }
  }, [catalogDefaultsValues]);
  useEffect(() => {
    const temp = detailStepsFailedLVS.map((item) => {
      return {
        ...item,
        correctedValue:
          catalogDefaultsValuesLookup[item.step] != undefined
            ? String(catalogDefaultsValuesLookup[item.step])
            : undefined,
      };
    });
    setCorrectCatalogDefaultsDetailSteps(temp);
  }, [detailStepsFailedLVS, catalogDefaultsValuesLookup]);

  const columnDefinitions: TableProps.ColumnDefinition<ValidationAttributeDetailStep>[] =
    [
      {
        id: "step",
        header: "Validation Step",
        width: "15%",
        sortingField: "step",
        cell: (item) => item.step,
      },
      {
        id: RESULT_COLUMN_ID,
        header: "Result",
        width: "15%",
        sortingField: "result",
        sortingComparator: (
          o1: ValidationAttributeDetailStep,
          o2: ValidationAttributeDetailStep
        ) =>
          detailStepsResultWeight(o1.result) -
          detailStepsResultWeight(o2.result),
        editConfig: {
          ariaLabel: "result",
          editIconAriaLabel: "editable",
          editingCell: (item, { currentValue, setValue }) => {
            const value = currentValue ?? item.result;
            return (
              <Select
                autoFocus={true}
                expandToViewport={true}
                selectedOption={
                  [
                    { label: "Pass (w/ Exception)", value: "pass_exception" },
                    {
                      label: "One-Time Pass (w/ Exception)",
                      value: "pass_exception_one_time",
                    },
                    { label: "Fail", value: "fail" },
                  ].find((option) => option.value === value) ?? {
                    label:
                      value === "fail"
                        ? "Fail"
                        : value === "pass_exception_one_time"
                        ? "One-Time Pass (w/ Exception)"
                        : "Pass (w/ Exception)",
                    value: value,
                  }
                }
                onChange={(event) => {
                  setValue(event.detail.selectedOption.value);
                  handleEditSubmitNoClick(
                    item,
                    RESULT_COLUMN_ID,
                    event.detail.selectedOption.value
                  );
                }}
                options={[
                  { label: "Pass (Exception)", value: "pass_exception" },
                  {
                    label: "One-Time Pass (Exception)",
                    value: "pass_exception_one_time",
                  },
                  { label: "Fail", value: "fail" },
                ]}
              />
            );
          },
          disabledReason: (item) => {
            if (["pass", "not_applicable", "exception"].includes(item.result)) {
              return "You cannot edit these results";
            } else if (
              submittedPublishCorrectCatalogDefaults.some(
                (step) => item.step == step.step
              )
            ) {
              return "Step has been published using Correct Catalog Defaults";
            }
            return undefined;
          },
        },
        cell: (item) => {
          return (
            <span>
              {getIconForValidationAttribute(item.result)} &nbsp;
              {VALIDATION_ATTRIBUTE_RESULT[item.result]}
            </span>
          );
        },
      },
      {
        id: PASS_EXCEPTION_REASON_COLUMN_ID,
        header: "Pass Exception Reason",
        width: "25%",
        sortingField: "passExceptionReason",
        cell: (item) => {
          return ["pass_exception", "pass_exception_one_time"].includes(
            item.result
          ) && !item.passExceptionReason ? (
            <Input
              autoFocus={true}
              invalid={true}
              placeholder={"pass exception reason is required"}
              value={item.passExceptionReason || ""}
            />
          ) : !["pass_exception", "pass_exception_one_time"].includes(
              item.result
            ) ? (
            ""
          ) : (
            item.passExceptionReason
          );
        },
        isRowHeader: true,
        editConfig: {
          ariaLabel: "Name",
          editIconAriaLabel: "editable",
          errorIconAriaLabel: "Name Error",
          validation(item, value) {
            return validationPassExceptionReasonOnType(value);
          },
          editingCell: (item, { currentValue, setValue }) => {
            return (
              <Input
                autoFocus={true}
                invalid={!currentValue && !item.passExceptionReason}
                value={currentValue ?? item.passExceptionReason}
                onChange={(event) => {
                  setValue(event.detail.value);
                  handleEditSubmitNoClick(
                    item,
                    PASS_EXCEPTION_REASON_COLUMN_ID,
                    event.detail.value
                  );
                }}
              />
            );
          },
          disabledReason: (item) => {
            if (
              ["pass", "not_applicable", "exception", "fail"].includes(
                item.result
              )
            ) {
              return "You cannot change the reason of non pass exception";
            }
            return undefined;
          },
        },
      },
      {
        id: "reason",
        header: "Validation Reason",
        width: "25%",
        sortingField: "reason",
        cell: (item) => item.reason,
      },
      {
        id: "hint",
        header: "More Information",
        width: "20%",
        sortingField: "hint",
        cell: (item) => item.hint,
      },
      {
        id: "auditInfo",
        header: "Audit",
        sortingField: "lastUpdateTime",
        cell: (item) => {
          const formattedPassExceptionTime = item.lastUpdateTime
            ? serviceToDisplay(item.lastUpdateTime)
            : "";
          const extendedFormattedPassExceptionTime = item.lastUpdateTime
            ? serviceToDisplay(item.lastUpdateTime, "yyyy/MM/DD HH:mm [PT]")
            : "";
          return item.passExceptionRequester ? (
            <div>
              {formattedPassExceptionTime} {/* Space between elements */}
              <Popover
                dismissButton={false}
                position="top"
                size="large"
                triggerType="custom"
                content={`Last edit by ${
                  item.passExceptionRequester?.identity
                } ${
                  extendedFormattedPassExceptionTime
                    ? `on ${extendedFormattedPassExceptionTime}`
                    : ""
                }`}
              >
                <StatusIndicator type="info" />
              </Popover>
            </div>
          ) : (
            <></>
          );
        },
      },
    ];

  // Proxy object to sort and filtering without modifying detailSteps
  const [viewDetail, setViewDetail] = useState<ValidationAttributeDetailStep[]>(
    []
  );
  const [viewDetailFilters, setViewDetailFilters] =
    useState<PropertyFilterQuery>({ tokens: [], operation: "and" });
  const defaultSort: TableProps.SortingState<any> = {
    isDescending: true,
    sortingColumn: {
      sortingField: columnDefinitions[1].sortingField,
      sortingComparator: columnDefinitions[1].sortingComparator,
    },
  };
  const [viewDetailSortingState, setViewDetailSortingState] =
    useState<TableProps.SortingState<any>>(defaultSort);

  useEffect(() => syncViewDetailState(), [detailSteps]);

  const syncViewDetailState = () => {
    if (viewDetail.length === 0) {
      // Initialize viewDetail on load and sorting by default column
      setViewDetail(sortViewDetail(detailSteps ?? [], viewDetailSortingState));
    } else {
      // Track any changes made in detailSteps using step as key, and update viewDetail attributes without re-sorting
      const detailStepsIndex: Map<string, ValidationAttributeDetailStep> =
        new Map(
          (detailSteps ?? []).map((obj: ValidationAttributeDetailStep) => [
            obj.step,
            obj,
          ])
        );
      const updatedView = viewDetail.map(
        (detail) => detailStepsIndex.get(detail.step) || detail
      );
      setViewDetail(updatedView);
    }
  };

  // Filter results from detailSteps and populate viewDetail
  const filterViewDetail = (filters: PropertyFilterQuery) =>
    filters.tokens.length === 0
      ? detailSteps
      : detailSteps.filter((item) =>
          filters.operation === "and"
            ? filters.tokens.every((token: PropertyFilterToken) =>
                buildPropertyFilterPredicate(token, item)
              )
            : filters.tokens.some((token: PropertyFilterToken) =>
                buildPropertyFilterPredicate(token, item)
              )
        );

  const geViewDetailSortFieldName = (
    sortingState: TableProps.SortingState<any>
  ) => {
    if (!sortingState.sortingColumn.sortingField) {
      return RESULT_COLUMN_ID as keyof ValidationAttributeDetailStep;
    }
    return sortingState.sortingColumn
      .sortingField as keyof ValidationAttributeDetailStep;
  };

  const sortViewDetail = (
    data: ValidationAttributeDetailStep[],
    sortingState: TableProps.SortingState<any>
  ): ValidationAttributeDetailStep[] => {
    if (sortingState?.sortingColumn?.sortingComparator) {
      const sortFunc = sortingState.sortingColumn.sortingComparator;
      const isDescending = sortingState.isDescending ?? false;
      return [...data].sort((a, b) =>
        isDescending ? sortFunc(b, a) : sortFunc(a, b)
      );
    } else {
      const isDescending = sortingState.isDescending ?? false;
      return [...data].sort(
        (
          a: ValidationAttributeDetailStep,
          b: ValidationAttributeDetailStep
        ) => {
          const aVal =
            (a[geViewDetailSortFieldName(sortingState)] as string) ?? "";
          const bVal =
            (b[geViewDetailSortFieldName(sortingState)] as string) ?? "";
          return isDescending
            ? bVal.localeCompare(aVal)
            : aVal.localeCompare(bVal);
        }
      );
    }
  };

  const buildPropertyFilterPredicate = (
    token: PropertyFilterToken,
    item: ValidationAttributeDetailStep
  ) => {
    switch (token.operator) {
      case "=":
        return (
          item[token.propertyKey as keyof ValidationAttributeDetailStep] ===
          token.value
        );
      case ":":
        return (
          item[
            token.propertyKey as keyof ValidationAttributeDetailStep
          ] as string
        )
          .toLowerCase()
          .includes(token.value.toLowerCase());
      default:
        // Operator not supported
        return true;
    }
  };

  const RESULT_FILTER_OPTIONS: {
    propertyKey: string;
    label: string;
    value: string;
  }[] = [
    { propertyKey: "result", label: "Pass", value: "pass" },
    {
      propertyKey: "result",
      label: "Pass (w/ Exception)",
      value: "pass_exception",
    },
    {
      propertyKey: "result",
      label: "One-Time Pass (w/ Exception)",
      value: "pass_exception_one_time",
    },
    { propertyKey: "result", label: "Fail", value: "fail" },
    { propertyKey: "result", label: "Not applicable", value: "not_applicable" },
  ];

  // Autocomplete filter options to be used in the property filter
  const FILTER_OPTIONS: {
    propertyKey: string;
    label: string;
    value: string;
  }[] = useMemo(() => {
    return [
      ...RESULT_FILTER_OPTIONS,
      ...(detailSteps ?? []).map((step: ValidationAttributeDetailStep) => {
        return { propertyKey: "step", value: step.step, label: step.step };
      }),
    ];
  }, [detailSteps]);

  // USE THIS WITH sortingField in COLUMN_DEF, also make sure items in the the thing in the table
  const { items, collectionProps } = useCollection(detailSteps, {
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
  });

  const [selectedItems, setSelectedItems] = React.useState<
    ValidationAttributeDetailStep[]
  >([]);
  const columnDefinitionCorrectCatalogDefaults: TableProps.ColumnDefinition<ValidationAttributeDetailStep>[] =
    [
      {
        id: "step",
        header: "Validation Step",
        width: "15%",
        cell: (item) => item.step,
      },
      {
        id: "hint",
        header: "Current Value",
        width: "15%",
        cell: (item) => item.hint,
      },
      {
        id: "default value",
        header: "Corrected Value",
        width: "15%",
        cell: (item) => item.correctedValue,
      },
      {
        id: "reason",
        header: "Validation Reason",
        width: "25%",
        cell: (item) => item.reason,
      },
    ];

  function isNotPassExceptionReasonRelated(
    element: TableProps.ColumnDefinition<ValidationAttributeDetailStep>
  ) {
    return (
      element.id != PASS_EXCEPTION_REASON_COLUMN_ID && element.id != "auditInfo"
    );
  }

  function clearValidationModalState() {
    dispatch(setValidationModalState(validationModalInitialState));
    setEditHasBeenMade(false);
    setErrorAfterSave(false);
    setCancelModalVisible(false);
    setViewDetail([]);
    setViewDetailFilters({ tokens: [], operation: "and" });
    setViewDetailSortingState(defaultSort);
    dispatch(setCorrectCatalogDefaultsExpandedNotVisible());
    setConfirmCorrectCatalogDefaultsCheckBox(false);
    setCalledGetCorrectCatalogDefaults(false);
    setSelectedItems([]);
  }

  function validationPassExceptionReasonOnEditSubmit(
    passExceptionReason: string
  ) {
    if (!passExceptionReason) {
      return;
    } else if (passExceptionReason.length > 128) {
      throw Error("You have exceeded the maximum length allowed");
    } else if (passExceptionReason.trim().length == 0) {
      throw Error("You cannot submit an empty string as pass exception reason");
    }
  }

  function validationPassExceptionReasonOnType(passExceptionReason: string) {
    if (!passExceptionReason) {
      return undefined;
    } else if (passExceptionReason.length > 128) {
      return "You have exceeded the maximum length allowed";
    } else if (passExceptionReason.trim().length == 0) {
      return "You cannot submit an empty string as pass exception reason";
    }
    return undefined;
  }

  function userIsAuthorizedPassException(
    editableAttributes: AuthorizedAttribute[]
  ) {
    return editableAttributes.some(
      (attribute) =>
        attribute.attributeName === "create_pass_exception" &&
        attribute.authorized
    );
  }

  function userIsAuthorizedCorrectCatalogDefaults(
    editableAttributes: AuthorizedAttribute[]
  ) {
    return editableAttributes.some(
      (attribute) =>
        attribute.attributeName === "publish_correct_catalog_default" &&
        attribute.authorized
    );
  }

  const handleEditSubmit = async (
    currentItem: ValidationAttributeDetailStep,
    column: TableProps.ColumnDefinition<ValidationAttributeDetailStep>,
    newValue: unknown
  ) => {
    const newItem =
      column.id == RESULT_COLUMN_ID
        ? { ...currentItem, result: newValue as string }
        : {
            ...currentItem,
            passExceptionReason: newValue as string,
          };
    const updatedDetailSteps = detailSteps.map((item) =>
      item.step == currentItem.step ? newItem : item
    );
    if (column.id == PASS_EXCEPTION_REASON_COLUMN_ID) {
      validationPassExceptionReasonOnEditSubmit(
        newItem?.passExceptionReason || ""
      );
    }
    if (
      JSON.stringify(initialDetailSteps) === JSON.stringify(updatedDetailSteps)
    ) {
      setEditHasBeenMade(false);
    } else {
      setEditHasBeenMade(true);
    }
    dispatch(
      setValidationModalState({
        isOpen: isOpen,
        detailSteps: updatedDetailSteps as ValidationAttributeDetailStep[],
        initialDetailSteps: initialDetailSteps,
        detailStepsFailedLVS: detailStepsFailedLVS,
        title: `${service} Results for ASIN ${asin?.asin} in Marketplace ${asin?.marketplace}`,
        asin: asin,
        service: service,
        waitingPassException: waitingPassException,
        receivedPassException: receivedPassException,
        correctCatalogDefaultsExpandedVisible:
          correctCatalogDefaultsExpandedVisible,
        passExceptionExpandedVisible: passExceptionExpandedVisible,
        waitingGetCorrectCatalogDefaults: waitingGetCorrectCatalogDefaults,
        receivedGetCorrectCatalogDefaults: receivedGetCorrectCatalogDefaults,
        waitingPublishCorrectCatalogDefaults:
          waitingPublishCorrectCatalogDefaults,
        receivedPublishCorrectCatalogDefaults:
          receivedPublishCorrectCatalogDefaults,
        catalogDefaultsValues: catalogDefaultsValues,
        catalogDefaultsValuesLookup: catalogDefaultsValuesLookup,
        submittedPublishCorrectCatalogDefaults:
          submittedPublishCorrectCatalogDefaults,
      })
    );
  };

  const handleEditSubmitNoClick = async (
    currentItem: ValidationAttributeDetailStep,
    column: string,
    newValue: unknown
  ) => {
    const newItem =
      column == RESULT_COLUMN_ID
        ? { ...currentItem, result: newValue as string }
        : {
            ...currentItem,
            passExceptionReason: newValue as string,
          };
    const updatedDetailSteps = detailSteps.map((item) =>
      item.step == currentItem.step ? newItem : item
    );
    if (column == PASS_EXCEPTION_REASON_COLUMN_ID) {
      validationPassExceptionReasonOnEditSubmit(
        newItem?.passExceptionReason || ""
      );
    }
    if (
      JSON.stringify(initialDetailSteps) === JSON.stringify(updatedDetailSteps)
    ) {
      setEditHasBeenMade(false);
    } else {
      setEditHasBeenMade(true);
    }
    dispatch(
      setValidationModalState({
        isOpen: isOpen,
        detailSteps: updatedDetailSteps as ValidationAttributeDetailStep[],
        initialDetailSteps: initialDetailSteps,
        detailStepsFailedLVS: detailStepsFailedLVS,
        title: `${service} Results for ASIN ${asin?.asin} in Marketplace ${asin?.marketplace}`,
        asin: asin,
        service: service,
        waitingPassException: waitingPassException,
        receivedPassException: receivedPassException,
        correctCatalogDefaultsExpandedVisible:
          correctCatalogDefaultsExpandedVisible,
        passExceptionExpandedVisible: passExceptionExpandedVisible,
        waitingGetCorrectCatalogDefaults: waitingGetCorrectCatalogDefaults,
        receivedGetCorrectCatalogDefaults: receivedGetCorrectCatalogDefaults,
        waitingPublishCorrectCatalogDefaults:
          waitingPublishCorrectCatalogDefaults,
        receivedPublishCorrectCatalogDefaults:
          receivedPublishCorrectCatalogDefaults,
        catalogDefaultsValues: catalogDefaultsValues,
        catalogDefaultsValuesLookup: catalogDefaultsValuesLookup,
        submittedPublishCorrectCatalogDefaults:
          submittedPublishCorrectCatalogDefaults,
      })
    );
  };

  const columnDefinitionsNoPassExceptionSet = columnDefinitions.filter(
    isNotPassExceptionReasonRelated
  );
  return (
    <>
      <Modal
        onDismiss={() => {
          setCancelModalVisible(false);
          dispatch(showTableModal());
        }}
        visible={cancelModalVisible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => {
                  setCancelModalVisible(false);
                  dispatch(showTableModal());
                }}
              >
                Go Back
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  clearValidationModalState();
                }}
              >
                Yes (all changes will be lost)
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Cancel Edit"
      >
        <Alert statusIconAriaLabel="Warning" type="warning">
          Are you sure that you want to cancel edit? The changes that you made
          won't be saved.
        </Alert>
      </Modal>
      <Modal
        onDismiss={() => {
          if (editHasBeenMade) {
            dispatch(hideTableModal());
            setCancelModalVisible(true);
          } else {
            clearValidationModalState();
          }
        }}
        data-dialog-sticky-header={true}
        visible={isOpen}
        closeAriaLabel="Close modal"
        size="max"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => {
                  trackButtonClick("validationTable", "closeModal", {});
                  if (editHasBeenMade) {
                    dispatch(hideTableModal());
                    setCancelModalVisible(true);
                  } else {
                    clearValidationModalState();
                  }
                }}
              >
                Close
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={<React.Fragment>{title}</React.Fragment>}
      >
        {link}
        {service === "LVS" ? (
          <ExpandableSection
            headerText={
              "Attribute Validation (Eligible for Default Value Corrections)"
            }
            expanded={correctCatalogDefaultsExpandedVisible}
            onChange={(event) => {
              if (event.detail.expanded) {
                dispatch(setCorrectCatalogDefaultsExpandedVisible());
              } else {
                dispatch(setCorrectCatalogDefaultsExpandedNotVisible());
              }
            }}
          >
            {lvsRanWithin24Hours &&
            userIsAuthorizedCorrectCatalogDefaults(editableAttributes) ? (
              <>
                <Alert statusIconAriaLabel="Info">
                  Please select the attributes you'd like to fix and submit to
                  Catalog. If you do not see your FAILED attributes, it's likely
                  out of scope. Visit{" "}
                  <Link
                    external
                    externalIconAriaLabel="Opens in a new tab"
                    href="https://w.amazon.com/bin/view/PSLM/Working_with_us/ASIN_Central/Validation/#HCurrentAutomatedValidations"
                  >
                    Devices ASIN Central Wiki
                  </Link>{" "}
                  for more information.
                </Alert>
                {hasCorrectValueBlank && (
                  <Alert statusIconAriaLabel="Info">
                    Default Value Corrections can only delete contributions from
                    specific Device Vendors and Retail Merchants. After
                    submitting your request, review Selection Central and
                    manually delete contributions from other contributors as
                    needed.
                  </Alert>
                )}
              </>
            ) : (
              <></>
            )}
            {!userIsAuthorizedCorrectCatalogDefaults(editableAttributes) ? (
              <Alert statusIconAriaLabel="Warning" type="warning">
                You do not have permissions to use this tool. Please create
                ticket to make request to LEAP team
              </Alert>
            ) : !lvsRanWithin24Hours ? (
              <Alert statusIconAriaLabel="Warning" type="warning">
                LVS Validation must have run within the last 24 hours. LVS
                Validation last ran on {lvsAuditDate || '"unknown date"'}
              </Alert>
            ) : waitingGetCorrectCatalogDefaults &&
              detailStepsFailedLVS.length != 0 ? (
              <Spinner></Spinner>
            ) : receivedPublishCorrectCatalogDefaults ? (
              <>
                <Alert statusIconAriaLabel="Info" type="info">
                  Submitted Publish Correct Catalog Defaults
                  {selectedItems.map((item) => {
                    return (
                      <TextContent>
                        {" "}
                        "{item.step}" : "{item.correctedValue}"
                      </TextContent>
                    );
                  })}
                </Alert>
              </>
            ) : (
              <Container>
                <SpaceBetween size={"s"} direction={"vertical"}>
                  {catalogDefaultsValues || detailStepsFailedLVS.length == 0 ? (
                    <>
                      <Table
                        onSelectionChange={(event) =>
                          setSelectedItems(event.detail.selectedItems)
                        }
                        selectedItems={selectedItems}
                        isItemDisabled={(item) =>
                          !LVS_ATTRIBUTES_IN_SCOPE.has(item.step) ||
                          !item.correctedValue
                        }
                        ariaLabels={{
                          selectionGroupLabel: "Items selection",
                        }}
                        columnDefinitions={
                          columnDefinitionCorrectCatalogDefaults
                        }
                        items={correctCatalogDefaultsDetailSteps || []}
                        selectionType="multi"
                      />
                      <Box float="right">
                        <span>
                          <SpaceBetween
                            size={"s"}
                            direction={"horizontal"}
                            alignItems={"center"}
                          >
                            {waitingPublishCorrectCatalogDefaults ? (
                              <Spinner></Spinner>
                            ) : (
                              <>
                                <Checkbox
                                  children={"Confirm Catalog Contribution"}
                                  checked={
                                    confirmCorrectCatalogDefaultsCheckBox
                                  }
                                  onChange={(event) =>
                                    setConfirmCorrectCatalogDefaultsCheckBox(
                                      event.detail.checked
                                    )
                                  }
                                />
                                <Button
                                  variant="primary"
                                  disabled={
                                    !confirmCorrectCatalogDefaultsCheckBox ||
                                    selectedItems.length == 0
                                  }
                                  onClick={() => {
                                    trackButtonClick(
                                      "publishCorrectCatalogDefault",
                                      "Submit",
                                      {
                                        asin: asin?.asin,
                                        marketplace: asin?.marketplace,
                                        obfuscatedMarketplaceId:
                                          asin?.obfuscatedMarketplaceId,
                                      }
                                    );
                                    dispatch(
                                      awaitingPublishCorrectCatalogDefaults()
                                    );
                                    dispatch(
                                      publishCorrectCatalogDefault(
                                        asin,
                                        selectedItems
                                      )
                                    );
                                    dispatch(
                                      setSubmittedPublishCorrectCatalogDefaults(
                                        selectedItems
                                      )
                                    );
                                  }}
                                >
                                  Submit Catalog Defaults
                                </Button>
                              </>
                            )}
                          </SpaceBetween>
                        </span>
                      </Box>
                    </>
                  ) : (
                    <Alert statusIconAriaLabel="Error" type={"error"}>
                      Did not Receive Catalog Default Values for Marketplace:"
                      {asin?.marketplace}" ProductLineId:"{asin?.productLineId}"
                      Party:"{asin?.asinParty}" Asin Type:"{asin?.asinType}"
                      Item Type:"{asin?.itemType}"
                    </Alert>
                  )}
                </SpaceBetween>
              </Container>
            )}
          </ExpandableSection>
        ) : (
          <></>
        )}
        <ExpandableSection
          headerText={"Attribute Validation (Eligible for Pass Exceptions)"}
          expanded={passExceptionExpandedVisible}
          onChange={(event) => {
            if (event.detail.expanded) {
              dispatch(setPassExceptionExpandedVisible());
            } else {
              dispatch(setPassExceptionExpandedNotVisible());
            }
          }}
        >
          {receivedPassException ? (
            <>
              <Alert statusIconAriaLabel="Info" type="info">
                Submitted Pass Exception
                {detailSteps
                  .filter((step) => {
                    const initialStep = initialDetailStepsHashMap.get(
                      step.step
                    );
                    if (
                      ["pass_exception", "pass_exception_one_time"].includes(
                        step.result
                      )
                    ) {
                      return step != initialStep;
                    } else {
                      return step.result != initialStep?.result;
                    }
                  })
                  .map((item) => {
                    return (
                      <TextContent>
                        {" "}
                        "{item.step}" : "{item.result}"
                      </TextContent>
                    );
                  })}
              </Alert>
            </>
          ) : (
            <Container>
              <SpaceBetween size={"s"} direction={"vertical"}>
                {errorAfterSave ? (
                  <Alert
                    statusIconAriaLabel={"Error"}
                    type={"error"}
                    dismissible={false}
                  >
                    Cannot save due to one or more pass exception reason(s)
                    being empty. Please fill in the empty pass exception
                    reason(s) and save again.
                  </Alert>
                ) : (
                  <></>
                )}

                <Table
                  {...collectionProps}
                  enableKeyboardNavigation={true}
                  sortingColumn={viewDetailSortingState?.sortingColumn}
                  sortingDescending={viewDetailSortingState?.isDescending}
                  filter={
                    <PropertyFilter
                      query={viewDetailFilters}
                      onChange={({ detail }) => {
                        setViewDetailFilters(detail);
                        setViewDetail(
                          sortViewDetail(
                            filterViewDetail(detail),
                            viewDetailSortingState
                          )
                        );
                      }}
                      i18nStrings={PROPERTY_FILTER_I18N}
                      customGroupsText={[]}
                      freeTextFiltering={{
                        operators: [":"],
                        defaultOperator: ":",
                      }}
                      filteringProperties={[
                        {
                          key: "step",
                          operators: ["="],
                          propertyLabel: "Validation Step",
                          groupValuesLabel: "Validation Step Value",
                        },
                        {
                          key: "result",
                          operators: ["="].map((operator) => ({
                            operator,
                            format: (value: string) => {
                              const option = RESULT_FILTER_OPTIONS.find(
                                (opt: {
                                  propertyKey: string;
                                  label: string;
                                  value: string;
                                }) => opt.value === value
                              );
                              return option?.label || value;
                            },
                          })),
                          propertyLabel: "Result",
                          groupValuesLabel: "Result values",
                        },
                      ]}
                      filteringOptions={FILTER_OPTIONS}
                      disableFreeTextFiltering={true}
                      expandToViewport={true}
                    />
                  }
                  onSortingChange={(event) => {
                    setViewDetailSortingState(event.detail);
                    setViewDetail(sortViewDetail(viewDetail, event.detail));
                  }}
                  columnDefinitions={
                    anyPassExceptions
                      ? columnDefinitions
                      : columnDefinitionsNoPassExceptionSet
                  }
                  items={viewDetail || []}
                  empty={<EmptyValidationTableState />}
                  resizableColumns={true}
                  submitEdit={handleEditSubmit}
                />
                <Box float="right">
                  <span>
                    <SpaceBetween
                      size={"s"}
                      direction={"horizontal"}
                      alignItems={"center"}
                    >
                      {waitingPassException ? (
                        <Spinner></Spinner>
                      ) : (
                        <>
                          <Button
                            variant="primary"
                            disabled={
                              !editHasBeenMade ||
                              !userIsAuthorizedPassException(editableAttributes)
                            }
                            onClick={() => {
                              trackButtonClick(
                                "validationTable",
                                "closeModal",
                                {}
                              );
                              if (anyErrorsInPassExceptionForm) {
                                setErrorAfterSave(true);
                              } else {
                                const updatedDetailSteps = detailSteps.filter(
                                  (step) => {
                                    const initialStep =
                                      initialDetailStepsHashMap.get(step.step);
                                    if (
                                      [
                                        "pass_exception",
                                        "pass_exception_one_time",
                                      ].includes(step.result)
                                    ) {
                                      return step != initialStep;
                                    } else {
                                      return step.result != initialStep?.result;
                                    }
                                  }
                                );
                                trackButtonClick("passException", "Submit", {
                                  asin: asin?.asin,
                                  marketplace: asin?.marketplace,
                                  obfuscatedMarketplaceId:
                                    asin?.obfuscatedMarketplaceId,
                                });
                                dispatch(awaitingPassException());
                                dispatch(
                                  createPassExceptionNew(
                                    asin,
                                    updatedDetailSteps,
                                    service
                                  )
                                );
                                setEditHasBeenMade(false);
                                setErrorAfterSave(false);
                                setCancelModalVisible(false);
                              }
                            }}
                          >
                            Create Pass Exception
                          </Button>
                        </>
                      )}
                    </SpaceBetween>
                  </span>
                </Box>
              </SpaceBetween>
            </Container>
          )}
        </ExpandableSection>
      </Modal>
    </>
  );
};
